import { Modal } from "@/app/(components)/Basic/Modal";
import { Button } from "@/app/(components)/Basic/Button";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import openToWorkApi from "@/api/art-order/openToWork/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { formatDateWithMonth } from "@/shared/dates";
import { ArtOrderCryptoValue } from "../../ArtOrder/ArtOrderCryptoValue";
import { ArtOrderDetailsItem } from "../../ArtOrder/ArtOrderDetailsItem";
import TagIcon from "@/assets/icons/tag-price.svg";
import { twMerge } from "tailwind-merge";
import { Checkbox } from "@/app/(components)/Basic/Checkbox";
import { ArtOrderBase } from "@/api/art-order/types";

export interface Props {
  artistId: string;
  isOpen: boolean;
  artOrders: ArtOrderBase[];
  className?: string;
  onSuccess: () => void;
  onClose: () => void;
}

const ChooseArtOrderModal = ({
  artistId,
  isOpen,
  artOrders,
  className,
  onSuccess,
  onClose,
}: Props) => {
  const queryClient = useQueryClient();

  const [orderIdsForInvite, setOrderIdsForInvite] = useState<string[]>([]);

  const handleCheckboxChange = (orderId: string) => {
    setOrderIdsForInvite((prevOrderIds) => {
      if (prevOrderIds.includes(orderId)) {
        return prevOrderIds.filter((id) => id !== orderId);
      } else {
        return [...prevOrderIds, orderId];
      }
    });
  };

  const { mutateAsync: sendInvite, isLoading } = useMutation({
    mutationFn: () =>
      openToWorkApi.sendInvitesToArtist(artistId, orderIdsForInvite),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["orders-for-otw-artists", artistId],
      });
      onSuccess();
    },
  });

  return (
    isOpen && (
      <>
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          title="Invite artist to art orders"
          smallPadding
          className="text-center"
          subtitle="Please select which art orders you wish to send the invite for:"
          centeredTitle
        >
          <div
            className={twMerge(
              className,
              "max-h-[300px] min-w-[500px] overflow-y-auto",
            )}
          >
            {artOrders.map((order) => (
              <div
                className={
                  "border-b p-2 cursor-pointer flex justify-between overflow-hidden"
                }
                key={order.id}
              >
                <div className="flex items-center">
                  <div className="ml-2 mr-6 items-center flex">
                    <Checkbox
                      name={order.id}
                      value={orderIdsForInvite.includes(order.id)}
                      handleChange={() => handleCheckboxChange(order.id)}
                      defaultClassName="light:bg-white light:border-endemicGray-300 border"
                      checkedClassName="endemic:fill-invert"
                    />
                  </div>
                  <label className="text-left flex flex-col" htmlFor={order.id}>
                    <span className="text-body-2">{order.title}</span>
                    <span className="text-body-3 text-endemicGray-700">
                      Created:{" "}
                      {formatDateWithMonth(order.createdAt!.toString())}
                    </span>
                  </label>
                </div>
                <div className="hidden md:block w-[150px]">
                  <div className="text-body-4">
                    <ArtOrderDetailsItem
                      icon={<TagIcon className="w-6 h-6 night:fill-white" />}
                      content={
                        <ArtOrderCryptoValue
                          cryptoValue={order.maxBudget}
                          paymentErc20TokenAddress={
                            order.budgetPaymentERC20TokenAddress
                          }
                          fiatPriceClassName="endemic:text-endemicGray-700"
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <Button onClick={onClose} className="mr-2">
              Cancel
            </Button>
            <Button
              endIcon={<ArrowRightIcon className="w-4" />}
              onClick={() => sendInvite()}
              className="ml-2"
              disabled={!orderIdsForInvite?.length || isLoading}
              disabledClassName="endemic:border-endemicGray-700 endemic:shadow-endemicGray-700 endemic:text-endemicGray-700"
            >
              {isLoading ? "Inviting" : "Send Invite"}
            </Button>
          </div>
        </Modal>
      </>
    )
  );
};

export default ChooseArtOrderModal;
